import { Helmet } from "react-helmet-async";
import { Scale } from "lucide-react";
import { WebsiteHeader } from "@/components/layout/website-header";
import { WebsiteFooter } from "@/components/layout/website-footer";

export default function TermsAndConditionsPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Helmet>
        <title>Terms and Conditions | SaaSGama</title>
        <meta name="description" content="Read SaaSGama's Terms and Conditions governing access to and use of the SaaSGama website, platform, and related services." />
        <meta name="keywords" content="SaaSGama Terms and Conditions, Terms of Service, Legal" />
        <link rel="canonical" href="https://saasgama.com/terms-and-conditions" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://saasgama.com/terms-and-conditions" />
        <meta property="og:site_name" content="SaaSGama" />
        <meta property="og:title" content="Terms and Conditions | SaaSGama" />
        <meta property="og:description" content="Read SaaSGama's Terms and Conditions governing access to and use of the SaaSGama website, platform, and related services." />
        <meta property="og:image" content="https://saasgama.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Terms and Conditions | SaaSGama" />
        <meta name="twitter:description" content="Read SaaSGama's Terms and Conditions governing access to and use of the SaaSGama website, platform, and related services." />
        <meta name="twitter:image" content="https://saasgama.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "Organization", "@id": "https://saasgama.com/#organization", "name": "SaaSGama", "url": "https://saasgama.com", "logo": "https://saasgama.com/logo.png" },
            { "@type": "BreadcrumbList", "@id": "https://saasgama.com/terms-and-conditions#breadcrumb", "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://saasgama.com" },
              { "@type": "ListItem", "position": 2, "name": "Terms and Conditions", "item": "https://saasgama.com/terms-and-conditions" }
            ]},
            { "@type": "WebPage", "@id": "https://saasgama.com/terms-and-conditions#webpage", "url": "https://saasgama.com/terms-and-conditions", "name": "Terms and Conditions | SaaSGama", "description": "Terms and Conditions governing access to and use of the SaaSGama website, platform, and related services.", "breadcrumb": { "@id": "https://saasgama.com/terms-and-conditions#breadcrumb" } }
          ]
        })}</script>
      </Helmet>

      <WebsiteHeader showBackButton />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 text-white">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Scale className="w-4 h-4" />
            Legal
          </div>
          <h1 className="text-4xl md:text-5xl font-bold">Terms and Conditions</h1>
        </div>
      </section>

      {/* Terms content */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12 space-y-10 text-gray-700 leading-relaxed">

            <section>
              <p>
                Welcome to SaaSGama. These Terms and Conditions govern your access to and use of the SaaSGama website,
                platform, and related services available at{" "}
                <a href="https://saasgama.com/" className="text-blue-600 hover:underline">https://saasgama.com/</a>.
                By accessing our website, creating an account, or using our services, you agree to these Terms and
                Conditions.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">1. About SaaSGama</h2>
              <p>
                SaaSGama provides software-as-a-service (SaaS) management tools designed to help businesses manage
                software subscriptions, track licenses, monitor usage, track renewals, and optimize software spending.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">2. Acceptance of Terms</h2>
              <p>
                By using SaaSGama, you confirm that you have read, understood, and agreed to these Terms and
                Conditions and our{" "}
                <a href="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</a>. If you do not
                agree with these terms, please discontinue use of the website and services.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">3. Eligibility and Account Registration</h2>
              <p>
                You must have the legal authority to enter into these Terms and Conditions. When creating an account,
                you agree to provide accurate, complete, and up-to-date information.
              </p>
              <p className="mt-2">
                You are responsible for maintaining the confidentiality of your login credentials and for activities
                conducted through your account. Please notify us promptly if you suspect unauthorized access to your
                account.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">4. Services and Features</h2>
              <p>SaaSGama may provide features including:</p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>SaaS subscription and software license management.</li>
                <li>Software usage monitoring and analytics.</li>
                <li>Subscription renewal reminders and tracking.</li>
                <li>License allocation and team management.</li>
                <li>Software spending insights and cost optimization recommendations.</li>
                <li>Reporting and integrations with supported applications.</li>
              </ul>
              <p className="mt-2">
                Features and availability may vary depending on your subscription plan. We may update, improve, or
                modify our services from time to time.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">5. Free Trials and Subscription Plans</h2>
              <p>
                SaaSGama may offer free trials and paid subscription plans, subject to the conditions displayed on our{" "}
                <a href="/pricing" className="text-blue-600 hover:underline">pricing page</a>.
              </p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>Trial duration and eligibility will be communicated at the time of registration or signup.</li>
                <li>Paid subscriptions will be charged according to the selected plan and billing frequency.</li>
                <li>Applicable taxes and additional charges will be disclosed where relevant before payment.</li>
                <li>Subscription renewals, cancellations, and plan changes will be governed by the terms presented during purchase.</li>
                <li>Any promotional or lifetime-access offer will be subject to its stated eligibility requirements and conditions.</li>
              </ul>
              <p className="mt-2">Please review the pricing and billing information carefully before purchasing a subscription.</p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">6. Payments and Billing</h2>
              <p>
                You agree to provide accurate billing information and authorize applicable charges for the
                subscription you select.
              </p>
              <p className="mt-2">
                If a payment fails, we may notify you and request updated payment information. Continued failure to
                pay may result in restricted access, suspension, or termination of your subscription, subject to
                applicable law and the terms of your plan.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">7. Cancellation and Refunds</h2>
              <p>
                You may cancel your subscription in accordance with the cancellation process and conditions applicable
                to your plan.
              </p>
              <p className="mt-2">
                Cancellation will take effect according to the billing terms presented at purchase. Any refund
                eligibility, including refunds for duplicate payments or billing errors, will be determined by the
                applicable refund policy and mandatory consumer protection laws.
              </p>
              <p className="mt-2">
                Please review our{" "}
                <a href="/refund-policy" className="text-blue-600 hover:underline">Refund Policy</a> before making a
                purchase. Where a separate refund policy is not available, contact our support team for clarification
                before purchasing.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">8. Acceptable Use</h2>
              <p>You agree not to:</p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>Use SaaSGama for unlawful, fraudulent, or unauthorized activities.</li>
                <li>Attempt to gain unauthorized access to the platform, accounts, or systems.</li>
                <li>Disrupt, damage, or interfere with the operation or security of our services.</li>
                <li>Copy, resell, distribute, or commercially exploit our services without written authorization.</li>
                <li>Upload malicious code or attempt to compromise the platform.</li>
                <li>Misuse integrations or access third-party systems without appropriate authorization.</li>
              </ul>
              <p className="mt-2">We reserve the right to investigate suspected violations and take appropriate action.</p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">9. Third-Party Services and Integrations</h2>
              <p>
                SaaSGama may integrate with third-party software, applications, and service providers. Your use of
                these services may be subject to their own terms, privacy policies, and conditions.
              </p>
              <p className="mt-2">
                We are not responsible for the availability, accuracy, security, or performance of third-party
                services beyond the responsibilities imposed on us by applicable law.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">10. Data and Privacy</h2>
              <p>
                We process personal information and other data in accordance with our{" "}
                <a href="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</a> and applicable
                data protection laws.
              </p>
              <p className="mt-2">
                You are responsible for ensuring that you have the necessary rights and permissions to provide any
                business, employee, subscription, or software usage information submitted to the platform.
              </p>
              <p className="mt-2">
                Please read our Privacy Policy to understand how information is collected, used, stored, and protected.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">11. Intellectual Property</h2>
              <p>
                All rights, title, and interest in the SaaSGama website, platform, software, design, branding, logos,
                text, and related materials belong to SaaSGama or its respective licensors, unless otherwise stated.
              </p>
              <p className="mt-2">
                You may not reproduce, modify, distribute, or use our intellectual property without prior written
                permission, except as permitted by applicable law.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">12. Service Availability</h2>
              <p>
                We aim to provide a reliable service but do not guarantee uninterrupted or error-free availability at
                all times. Maintenance, technical issues, third-party outages, or circumstances beyond our reasonable
                control may temporarily affect access.
              </p>
              <p className="mt-2">
                Where applicable, any specific service-level commitments will be governed by the relevant agreement or
                subscription plan.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">13. Disclaimers</h2>
              <p>
                SaaSGama provides tools, analytics, and recommendations to support software subscription and license
                management. Cost-saving estimates, forecasts, analytics, and recommendations are informational and
                may vary depending on actual usage, pricing, business requirements, and other factors.
              </p>
              <p className="mt-2">We do not guarantee specific savings, financial outcomes, or business results.</p>
              <p className="mt-2">
                To the extent permitted by applicable law, the services are provided subject to the warranties and
                conditions expressly stated in the applicable agreement.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">14. Limitation of Liability</h2>
              <p>
                To the extent permitted by applicable law, SaaSGama will not be liable for indirect, incidental,
                special, or consequential losses arising from the use of, or inability to use, the services.
              </p>
              <p className="mt-2">
                Nothing in these Terms and Conditions excludes or limits liability that cannot lawfully be excluded or
                limited. Any other limitation of liability will apply only to the extent permitted by applicable law
                and any applicable customer agreement.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">15. Suspension and Termination</h2>
              <p>
                We may suspend or terminate access to SaaSGama if you materially violate these Terms and Conditions,
                engage in fraudulent or unlawful activities, or create a security risk.
              </p>
              <p className="mt-2">
                Where reasonably practicable and legally permitted, we will provide notice and an opportunity to
                address the issue. Any consequences for paid subscriptions will be handled in accordance with the
                applicable subscription terms and law.
              </p>
              <p className="mt-2">
                You may stop using our services or cancel your subscription according to the applicable cancellation
                terms.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">16. Changes to These Terms</h2>
              <p>
                We may update these Terms and Conditions from time to time. Updated terms will be published on this
                page with a revised effective date.
              </p>
              <p className="mt-2">
                Your continued use of the services after updated terms become effective constitutes acceptance of
                those changes where permitted by law.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">17. Governing Law</h2>
              <p>
                These Terms and Conditions will be governed by the applicable laws of the jurisdiction in which the
                responsible SaaSGama operating entity is established, subject to any mandatory laws that apply to you.
              </p>
              <p className="mt-2">
                Any disputes will be handled by the competent courts or dispute resolution bodies having jurisdiction
                under applicable law.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">18. Contact Information</h2>
              <p>
                If you have any questions about these Terms and Conditions, subscription plans, billing, or our
                services, please contact us through the official contact details published on our website.
              </p>
              <div className="mt-2 space-y-1">
                <p><strong>Website:</strong> <a href="https://saasgama.com/" className="text-blue-600 hover:underline">https://saasgama.com/</a></p>
                <p><strong>Email:</strong> hello@saasgama.com</p>
                <p><strong>Company Name:</strong> SaaSGama Inc.</p>
                <p><strong>Business Address:</strong> 5 Shenton Wy, #10-01, UIC Building, Singapore 068808</p>
              </div>
              <p className="mt-4">
                By using SaaSGama, you acknowledge that you have read and understood these Terms and Conditions.
              </p>
            </section>

          </div>
        </div>
      </section>

      <WebsiteFooter />
    </div>
  );
}
